/**
 * Task-related data fetchers
 */
import {
  useQueryClient,
  QueryClient,
  useQuery,
  useMutation,
  useMutationState,
  queryOptions,
} from "@tanstack/react-query";

import { resetSplitDocumentConfidence } from "@/src/actions/file";
import {
  updateStatus,
  updateTask,
  updateAssignedUser,
  unassignAllMyTasks,
  updateTaskNote,
  updateTaskTags,
  updatePriority,
  deleteTask,
} from "@/src/actions/task";
import { ToastAction } from "@/src/components/shadcn/ui/toast";
import { useTaskFilters, type TaskFilters } from "@/src/components/tasks/use-task-filters";
import { toast } from "@/src/hooks/use-toast";
import { isTaskAgentRunActive, TASK_AGENT_RUN_POLL_INTERVAL_MS } from "@/src/lib/data-fetchers/agent-fetchers";
import { PAGINATED_ORDERS_CACHE_KEY, SINGLE_ORDER_CACHE_KEY } from "@/src/lib/data-fetchers/order-query-keys";
import { logger } from "@/src/lib/logging/logger";
import { type ClassificationSection } from "@/src/models/constants/classifications";
import { FileType } from "@/src/models/file";
import { BlockKey } from "@/src/models/intake/blocks/block-types";
import { Patient } from "@/src/models/patient";
import type { ConfirmInsuranceNetworkRequest } from "@/src/models/provider-insurance-requirements";
import { SchedulingRecommendation } from "@/src/models/scheduling-recommendation";
import type { VerifySchedulingRecommendationInput } from "@/src/models/scheduling-recommendation";
import { Section } from "@/src/models/section";
import { Task, TaskStatus, TaskPriority, TaskType, pastTenseStatus, completedStatuses } from "@/src/models/task";
import { type TaskFacetCountsKey, type TaskFiltersParams } from "@/src/models/task-filters";
import { TaskMetadata, taskToTaskMetadata } from "@/src/models/task-metadata";
import { type UpsertTaskStepVerification } from "@/src/models/task-step-verification";
import { type VerificationResult } from "@/src/models/task-verification";
import { confirmInsuranceNetwork } from "@/src/requests/task";
import {
  getAllFileTasks,
  getCanResumeAwaitingInformationTask,
  getWorkflowStepResumeCheck,
  getChildTasks,
  getTaskById,
  getTasksMetadata,
  getPaginatedTasksMetadata,
  getDocumentClassificationFilterSlugs,
  getTaskFacetCounts,
  getTaskHistory,
  getTaskSchedulingRecommendation,
  getTaskApproval,
  approveTask,
  continueAfterManualTprStep,
  getTaskStepVerifications,
  overrideTprReceivingAssignment,
  upsertTaskStepVerification,
  regenerateTaskSchedulingRecommendation,
  resumeAwaitingInformationTask,
  resumeWorkflowStep,
  verifyAndComplete,
  verifySchedulingEligibilityTask,
  sendReferringProviderEmail,
  type PaginatedTasksMetadataResponse,
} from "@/src/requests/task";
import { fetchJSON, isNotFound } from "@/src/requests/utils/fetch-json";

// Covers every task a patient realistically accumulates, in one page.
const PATIENT_TASKS_PAGE_SIZE = 500;

export const TASK_CACHE_KEY = "task";
export const TASK_BY_ID_CACHE_KEY = "task-by-id";
export const TASKS_CACHE_KEY = "tasks";
export const PAGINATED_TASKS_METADATA_CACHE_KEY = "paginated-tasks-metadata";
export const TASK_CLASSIFICATION_FILTER_SLUGS_CACHE_KEY = "task-classification-filter-slugs";
export const TASK_FACET_COUNTS_CACHE_KEY = "task-facet-counts";
export const CONTINUE_TASK_MUTATION_KEY = ["continue-task"] as const;
export const TASK_COMPLETION_MUTATION_KEY = ["task-completion"] as const;
export const TASK_APPROVAL_CACHE_KEY = "task-approval";
export const TASK_STEP_VERIFICATION_CACHE_KEY = "task-step-verification";
export const CLASSIFICATIONS_QUERY_KEY = ["classifications"] as const;
export const getTaskSchedulingRecommendationQueryKey = (taskId: string | null | undefined) =>
  ["taskSchedulingRecommendation", taskId] as const;

export const TASK_CHILDREN_CACHE_KEY = "task-children";

export function useTaskRelativesLookup() {
  const queryClient = useQueryClient();

  return {
    fetchTaskById: (taskId: string) =>
      queryClient.fetchQuery({ queryKey: [TASK_BY_ID_CACHE_KEY, taskId], queryFn: () => getTaskById(taskId) }),
    fetchChildTasks: (taskId: string) =>
      queryClient.fetchQuery({ queryKey: [TASK_CHILDREN_CACHE_KEY, taskId], queryFn: () => getChildTasks(taskId) }),
  };
}

export function useTaskApproval(taskId: string | null | undefined, taskVersion?: string) {
  return useQuery({
    queryKey: [TASK_APPROVAL_CACHE_KEY, taskId, taskVersion],
    queryFn: () => getTaskApproval(taskId!),
    enabled: Boolean(taskId),
  });
}

export function useApproveTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, approved }: { taskId: string; approved: boolean }) => approveTask(taskId, { approved }),
    onSuccess: async (_result, { taskId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [TASK_APPROVAL_CACHE_KEY, taskId] }),
        queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY] }),
        queryClient.invalidateQueries({ queryKey: [TASKS_CACHE_KEY] }),
      ]);
    },
    meta: {
      invalidates: [[TASK_APPROVAL_CACHE_KEY], [TASK_CACHE_KEY], [TASKS_CACHE_KEY]],
    },
  });
}

export function useContinueAfterManualTprStepMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, stepKey, bypass }: { taskId: string; stepKey: string; bypass?: boolean }) =>
      continueAfterManualTprStep(taskId, stepKey, bypass),
    onSuccess: async (_result, { taskId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY, taskId] }),
        queryClient.invalidateQueries({ queryKey: [TASK_BY_ID_CACHE_KEY, taskId] }),
        queryClient.invalidateQueries({ queryKey: [TASKS_CACHE_KEY] }),
      ]);
    },
    meta: {
      invalidates: [[TASK_CACHE_KEY], [TASK_BY_ID_CACHE_KEY], [TASKS_CACHE_KEY]],
    },
  });
}

/**
 * Recorded Correct / Not correct verdicts for this task's step cards, keyed by stable `stepKey`.
 */
export function useTaskStepVerifications(taskId: string | null | undefined) {
  const { data, isPending } = useQuery({
    queryKey: [TASK_STEP_VERIFICATION_CACHE_KEY, taskId],
    queryFn: () => getTaskStepVerifications(taskId as string),
    enabled: Boolean(taskId),
  });

  return {
    verdictByStepKey: new Map((data ?? []).map((row) => [row.stepKey, row])),
    isPending,
  };
}

/**
 * Applies ops' provider/location override and replays the provider-scoped decisions downstream.
 * Invalidates the task caches because the workflow goes back to PROCESSING and the step rows the
 * instructions render from are rewritten server-side.
 */
export function useOverrideTprReceivingAssignmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      providerNpi,
      targetDepartmentId,
    }: {
      taskId: string;
      providerNpi: string;
      targetDepartmentId: string;
    }) => overrideTprReceivingAssignment(taskId, { providerNpi, targetDepartmentId }),
    onSuccess: async (_result, { taskId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY, taskId] }),
        queryClient.invalidateQueries({ queryKey: [TASK_BY_ID_CACHE_KEY, taskId] }),
        queryClient.invalidateQueries({ queryKey: [TASKS_CACHE_KEY] }),
        // The replay clears the verdicts on every card it recomputes; a cached CORRECT would
        // otherwise keep those cards Completed with no controls until the page remounts.
        queryClient.invalidateQueries({ queryKey: [TASK_STEP_VERIFICATION_CACHE_KEY, taskId] }),
      ]);
    },
  });
}

export function useUpsertTaskStepVerificationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: UpsertTaskStepVerification }) =>
      upsertTaskStepVerification(taskId, payload),
    onSuccess: async (_result, { taskId }) => {
      await queryClient.invalidateQueries({ queryKey: [TASK_STEP_VERIFICATION_CACHE_KEY, taskId] });
    },
  });
}

async function getClassifications(): Promise<ClassificationSection[]> {
  return fetchJSON<ClassificationSection[]>("/api/classifications");
}

export function getClassificationsQueryOptions() {
  return queryOptions({
    queryKey: CLASSIFICATIONS_QUERY_KEY,
    queryFn: getClassifications,
    staleTime: Infinity,
  });
}

export function useClassifications() {
  return useQuery(getClassificationsQueryOptions());
}

const defaultFilters = { showCompleted: false, filterOutsideSla: false };
const defaultColumnFilters = {
  status: undefined,
  type: undefined,
  assignedToUser: undefined,
  priority: undefined,
  tags: undefined,
  documentClassification: undefined,
  source: undefined,
  authorizationNumber: undefined,
};

export function prefetchPaginatedTasksMetadata(queryClient: QueryClient) {
  queryClient.prefetchQuery({
    queryKey: [
      PAGINATED_TASKS_METADATA_CACHE_KEY,
      defaultFilters,
      defaultColumnFilters,
      0,
      30,
      "createdAt",
      "desc",
      undefined,
    ],
    queryFn: () =>
      getPaginatedTasksMetadata({
        page: 0,
        pageSize: 30,
        showCompleted: defaultFilters.showCompleted,
        status: defaultColumnFilters.status,
        type: defaultColumnFilters.type,
        assignedToUser: defaultColumnFilters.assignedToUser,
        priority: defaultColumnFilters.priority,
        tags: defaultColumnFilters.tags,
        documentClassification: defaultColumnFilters.documentClassification,
        source: defaultColumnFilters.source,
        authorizationNumber: defaultColumnFilters.authorizationNumber,
        sortBy: "createdAt",
        sortOrder: "desc",
        filterOutsideSla: defaultFilters.filterOutsideSla,
      }),
  });
}

export function prefetchDocumentClassificationFilterSlugs(queryClient: QueryClient) {
  queryClient.prefetchQuery({
    queryKey: [TASK_CLASSIFICATION_FILTER_SLUGS_CACHE_KEY],
    queryFn: getDocumentClassificationFilterSlugs,
    staleTime: Infinity,
  });
}

export function useDocumentClassificationFilterSlugs() {
  return useQuery({
    queryKey: [TASK_CLASSIFICATION_FILTER_SLUGS_CACHE_KEY],
    queryFn: getDocumentClassificationFilterSlugs,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * Custom hook for paginated task metadata fetching using the tasks_metadata view
 */
export function usePaginatedTasksMetadata({
  filters,
  columnFilters,
  page = 0,
  pageSize = 30,
  prefetchNextPage = true,
  sortBy,
  sortOrder,
  query,
}: {
  filters?: TaskFilters;
  columnFilters?: {
    status?: string[];
    type?: string[];
    assignedToUser?: string[];
    priority?: string[];
    tags?: string[];
    documentClassification?: string[];
    source?: string[];
    authorizationNumber?: string;
  };
  page?: number;
  pageSize?: number;
  prefetchNextPage?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  query?: string;
} = {}) {
  const [defaultTaskFilters] = useTaskFilters();
  const queryClient = useQueryClient();
  const pendingTaskCompletionIds = [
    ...useMutationState<string | null>({
      filters: {
        mutationKey: CONTINUE_TASK_MUTATION_KEY,
        status: "pending",
      },
      select: (mutation) => {
        const variables = mutation.state.variables as { isFinalSection?: boolean; task?: { id?: string } } | undefined;

        return variables?.isFinalSection ? (variables.task?.id ?? null) : null;
      },
    }),
    // Covers "Complete"/"Correct"/"Incorrect" actions (useTaskStatusMutation, useVerifyAndCompleteMutation):
    // hide the task from the polled list while its completion request is in flight so a poll landing
    // before the write does can't clobber the optimistic state and make the task look incomplete again.
    ...useMutationState<string | null>({
      filters: {
        mutationKey: TASK_COMPLETION_MUTATION_KEY,
        status: "pending",
      },
      select: (mutation) => {
        const variables = mutation.state.variables as { taskId?: string; newStatus?: TaskStatus } | undefined;

        if (!variables?.taskId) return null;
        if ("newStatus" in variables && variables.newStatus !== TaskStatus.COMPLETED) return null;

        return variables.taskId;
      },
    }),
  ].filter((taskId): taskId is string => taskId !== null);

  const taskFilters = {
    ...defaultTaskFilters,
    ...filters,
  };

  const tasksQuery = useQuery({
    queryKey: [
      PAGINATED_TASKS_METADATA_CACHE_KEY,
      taskFilters,
      columnFilters,
      page,
      pageSize,
      sortBy,
      sortOrder,
      query,
    ],
    queryFn: () =>
      getPaginatedTasksMetadata({
        page,
        pageSize,
        showCompleted: taskFilters.showCompleted,
        filterOutsideSla: taskFilters.filterOutsideSla,
        status: columnFilters?.status,
        type: columnFilters?.type,
        assignedToUser: columnFilters?.assignedToUser,
        priority: columnFilters?.priority,
        tags: columnFilters?.tags,
        documentClassification: columnFilters?.documentClassification,
        source: columnFilters?.source,
        authorizationNumber: columnFilters?.authorizationNumber,
        sortBy,
        sortOrder,
        query,
      }),
    staleTime: 5 * 1000,
    refetchInterval: 5 * 1000,
    // Keep previous data while refetching to prevent UI flicker when totalCount temporarily sets to 0
    placeholderData: (previousData) => previousData,
  });

  const paginatedData: PaginatedTasksMetadataResponse | undefined = tasksQuery.data;
  const tasksMap: Record<string, TaskMetadata> = paginatedData?.tasks || {};
  const tasks = Object.values(tasksMap).filter((task) => !pendingTaskCompletionIds.includes(task.id));
  const visibleTasksMap = Object.fromEntries(tasks.map((task) => [task.id, task]));
  const hiddenPendingTaskCount = Object.keys(tasksMap).length - tasks.length;

  const hasMore = (!tasksQuery?.isFetching && paginatedData?.hasMore) ?? false;

  if (prefetchNextPage && hasMore) {
    queryClient.prefetchQuery({
      queryKey: [
        PAGINATED_TASKS_METADATA_CACHE_KEY,
        taskFilters,
        columnFilters,
        page + 1,
        pageSize,
        sortBy,
        sortOrder,
        query,
      ],
      queryFn: () =>
        getPaginatedTasksMetadata({
          page: page + 1,
          pageSize,
          showCompleted: taskFilters.showCompleted,
          filterOutsideSla: taskFilters.filterOutsideSla,
          status: columnFilters?.status,
          type: columnFilters?.type,
          assignedToUser: columnFilters?.assignedToUser,
          priority: columnFilters?.priority,
          tags: columnFilters?.tags,
          documentClassification: columnFilters?.documentClassification,
          source: columnFilters?.source,
          authorizationNumber: columnFilters?.authorizationNumber,
          sortBy,
          sortOrder,
          query,
        }),
      staleTime: 5 * 1000,
    });
  }

  return {
    tasksMap: visibleTasksMap,
    tasks,
    totalCount: Math.max(0, (paginatedData?.totalCount || 0) - hiddenPendingTaskCount),
    hasMore: paginatedData?.hasMore || false,
    page: paginatedData?.page || page,
    pageSize: paginatedData?.pageSize || pageSize,
    isFetching: tasksQuery.isFetching,
    isLoading: tasksQuery.isLoading,
    isError: tasksQuery.isError,
    refetch: tasksQuery.refetch,
  };
}

// Every facet's count is only ever consumed once its own filter dropdown is opened (to sort/badge
// its options), so each dimension is fetched independently and lazily. Pass `enabled` once the
// caller actually needs it (e.g. that filter's dropdown has been opened).
export function useTaskFacetCountQuery(
  dimension: TaskFacetCountsKey,
  filters: TaskFiltersParams = defaultFilters,
  enabled: boolean
) {
  return useQuery({
    queryKey: [TASK_FACET_COUNTS_CACHE_KEY, filters, [dimension]],
    queryFn: () => getTaskFacetCounts({ ...filters, facets: [dimension] }),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * A patient's tasks as metadata rows, which is all the patient views render. Passing
 * `unconfirmedName` also matches tasks whose patient is unconfirmed but whose placeholder name is
 * this patient's.
 * @param patientId Omit to disable the query.
 */
export function usePatientTaskMetadata(
  patientId?: string,
  unconfirmedName?: { givenName?: string | null; familyName?: string | null }
) {
  const patientGivenName = unconfirmedName?.givenName ?? undefined;
  const patientFamilyName = unconfirmedName?.familyName ?? undefined;

  const tasksQuery = useQuery({
    queryKey: [TASKS_CACHE_KEY, "patient", patientId, patientGivenName, patientFamilyName],
    queryFn: () =>
      getPaginatedTasksMetadata({
        page: 0,
        pageSize: PATIENT_TASKS_PAGE_SIZE,
        showCompleted: true,
        sortBy: "createdAt",
        sortOrder: "desc",
        patientId,
        patientGivenName,
        patientFamilyName,
      }),
    enabled: Boolean(patientId),
    staleTime: Infinity,
    refetchInterval: 5 * 1000,
  });

  const patientTaskMetadataMap: Record<string, TaskMetadata> = tasksQuery.data?.tasks || {};

  return {
    patientTaskMetadataMap,
    patientTaskMetadata: Object.values(patientTaskMetadataMap),
    isFetching: tasksQuery.isFetching,
    isLoading: tasksQuery.isLoading,
    isError: tasksQuery.isError,
    refetch: tasksQuery.refetch,
  };
}

export function useFileTasks(fileId?: string) {
  const tasksQuery = useQuery({
    queryKey: [TASKS_CACHE_KEY, "file", fileId],
    queryFn: () => {
      if (!fileId) {
        return {} as Awaited<ReturnType<typeof getAllFileTasks>>;
      }

      return getAllFileTasks(fileId);
    },
    enabled: Boolean(fileId),
    staleTime: Infinity,
    refetchInterval: 5 * 1000,
  });

  const fileTasksMap: Record<string, Task> = tasksQuery.data || {};

  return {
    fileTasksMap,
    fileTasks: Object.values(fileTasksMap).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }),
    isFetching: tasksQuery.isFetching,
    isLoading: tasksQuery.isLoading,
    isError: tasksQuery.isError,
    refetch: tasksQuery.refetch,
  };
}

/**
 * Hook to get matched patient tasks for a section
 * NOTE: Unlike old patient-section, this hook does not search over ALL tasks for placeholder patient name matches;
 * it only searches over the patient tasks for the matched patient, if there is one.
 */
export function useMatchedPatientTasksForSection(section: Section | null, taskDisplayId: string) {
  const matchedPatient = (() => {
    const patientBlock = section?.enrichedContent?.[BlockKey.PATIENT];

    if (!patientBlock) return null;
    const reviewedFields = patientBlock.reviewedFields;

    return reviewedFields?.id ? (reviewedFields as Patient) : null;
  })();

  const { patientTaskMetadata, isLoading: isLoadingPatientTasks } = usePatientTaskMetadata(matchedPatient?.id);

  const verifiedTasks = (() => {
    if (!patientTaskMetadata) return [];

    return patientTaskMetadata.filter((task) => {
      return !completedStatuses.includes(task.status as TaskStatus) && task.displayId !== taskDisplayId;
    });
  })();

  const matchedTasksUrl = (() => {
    return matchedPatient ? `/patients/${matchedPatient.displayId}/tasks` : null;
  })();

  return {
    isLoading: isLoadingPatientTasks,
    totalMatchedTasks: verifiedTasks.length,
    matchedTasksUrl,
  };
}

export function useTaskByDisplayId(displayId: string) {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  const {
    data: task,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [TASK_CACHE_KEY, displayId],
    queryFn: async () => {
      const filters = taskFilters;

      try {
        const task = await getTaskById(displayId);

        if (task) {
          updateTasksQueryData(queryClient, task, filters);
        }

        return task;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        throw err;
      }
    },
    // While the intake agent is actively running, its steps materialize new sections/enriched
    // content that this task fetch needs to pick up — poll alongside the agent-run query.
    refetchInterval: (query) => {
      if (query.state.data?.status === TaskStatus.PROCESSING) return TASK_AGENT_RUN_POLL_INTERVAL_MS;
      if (query.state.data?.type !== TaskType.INTAKE) return false;

      return isTaskAgentRunActive(queryClient, query.state.data.id) ? TASK_AGENT_RUN_POLL_INTERVAL_MS : false;
    },
  });

  return { task, isFetching, isLoading, isError };
}

export function useTaskById(id: string | null, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  const {
    data: task,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [TASK_BY_ID_CACHE_KEY, id],
    queryFn: async () => {
      if (!id) {
        return null;
      }

      const task = await getTaskById(id);

      if (task) {
        updateTasksQueryData(queryClient, task, taskFilters);
      }

      return task;
    },
    staleTime: Infinity,
    enabled: options?.enabled !== false && !!id,
  });

  return { task, isFetching, isLoading, isError };
}

export const useTasksMetadata = ({
  filters,
  columnFilters,
  sortBy,
  sortOrder,
  enabled = true,
}: {
  filters?: TaskFilters;
  columnFilters?: {
    status?: string[];
    type?: string[];
    assignedToUser?: string[];
    priority?: string[];
  };
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
} = {}) => {
  const query = useQuery({
    enabled,
    queryKey: ["tasksMetadata", filters, columnFilters, sortBy, sortOrder],
    queryFn: () =>
      getTasksMetadata({
        showCompleted: !!filters?.showCompleted,
        status: columnFilters?.status,
        type: columnFilters?.type,
        assignedToUser: columnFilters?.assignedToUser,
        priority: columnFilters?.priority,
        sortBy,
        sortOrder: sortOrder ?? "desc",
      }),
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  return {
    data: query.data,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Creates a generic onMutate handler for optimistic task updates.
 * This handler updates all relevant caches: the main tasks list,
 * the paginated metadata list, and individual task queries.
 * TODO: Once we completely get rid of getAllTasks, we can simplify the first half of the update func.
 *  and implement rollbacks for specific pages.
 */
const createOptimisticTaskUpdateHandler = <T extends { taskId: string }>(
  queryClient: QueryClient,
  filters: TaskFilters,
  updateFns: {
    /**
     * A function that computes the changes for the full Task object.
     * It will only be called if the task is found in the `TASKS_CACHE_KEY`.
     */
    task?: (variables: T, originalTask: Task) => Partial<Task>;
    /**
     * A function that computes the changes for the TaskMetadata object.
     * It will be called for each instance of the task found in any
     */
    metadata?: (variables: T, originalMetadata: TaskMetadata) => Partial<TaskMetadata>;
  }
) => {
  return async (variables: T) => {
    await queryClient.cancelQueries({ queryKey: [TASKS_CACHE_KEY] });

    await queryClient.cancelQueries({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] });

    // Snapshot the full task list for rollback.
    const previousTasks = queryClient.getQueryData<Record<string, Task>>([TASKS_CACHE_KEY, filters]);

    // Handle TASKS_CACHE_KEY updates (TODO: deprecate) and individual task caches
    if (updateFns.task) {
      const originalTask = previousTasks?.[variables.taskId];

      if (originalTask) {
        const taskChanges = updateFns.task(variables, originalTask);
        const updatedTask = { ...originalTask, ...taskChanges };

        // Use the shared updateTasksQueryData function
        updateTasksQueryData(queryClient, updatedTask, filters);
      }
    }

    // Handle PAGINATED_TASKS_METADATA_CACHE_KEY updates
    if (updateFns.metadata) {
      const paginatedQueries = queryClient.getQueryCache().findAll({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] });

      paginatedQueries.forEach((query) => {
        const originalData = queryClient.getQueryData<PaginatedTasksMetadataResponse>(query.queryKey);
        const originalMetadata = originalData?.tasks?.[variables.taskId];

        if (originalMetadata) {
          const metadataChanges = updateFns.metadata?.(variables, originalMetadata);

          queryClient.setQueryData<PaginatedTasksMetadataResponse>(query.queryKey, (oldData) => {
            if (!oldData) return undefined;

            return {
              ...oldData,
              tasks: {
                ...oldData.tasks,
                [variables.taskId]: {
                  ...originalMetadata,
                  ...metadataChanges,
                },
              },
            };
          });
        }
      });
    }

    return { previousTasks };
  };
};

/**
 * Updates the tasks query data with a new or updated task.
 * @param queryClient - The QueryClient instance.
 * @param updatedTask - The task to add or update in the cache.
 */
export const updateTasksQueryData = (queryClient: QueryClient, updatedTask: Task, filters: TaskFilters) => {
  // Update non-paginated tasks cache
  queryClient.setQueryData([TASKS_CACHE_KEY, filters], (oldData: Record<string, Task> = {}) => {
    return {
      ...oldData,
      [updatedTask.id]: updatedTask,
    };
  });

  // Update individual task caches
  queryClient.setQueryData([TASK_CACHE_KEY, updatedTask.displayId], updatedTask);

  queryClient.setQueryData([TASK_BY_ID_CACHE_KEY, updatedTask.id], updatedTask);
};

/**
 * Invalidates all task-related caches that can be impacted by updates.
 */
export const invalidateTaskCaches = (
  queryClient: QueryClient,
  task?: { id?: string | null; displayId?: string | null }
) => {
  const invalidations = [queryClient.invalidateQueries({ queryKey: [TASKS_CACHE_KEY] })];

  if (task?.displayId) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY, task.displayId] }));
  }

  if (task?.id) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: [TASK_BY_ID_CACHE_KEY, task.id] }));
  }

  invalidations.push(queryClient.invalidateQueries({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] }));
  invalidations.push(queryClient.invalidateQueries({ queryKey: [TASK_FACET_COUNTS_CACHE_KEY] }));

  return Promise.all(invalidations);
};

// Records the scheduling-eligibility verification and completes the verification task in one call.
// On success the task caches are invalidated so the (now completed) task drops out of the queue.
export function useTaskSchedulingRecommendation(taskId: string | null | undefined, taskUpdatedAt?: string | null) {
  const queryKey = [...getTaskSchedulingRecommendationQueryKey(taskId), taskUpdatedAt] as const;
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!taskId) {
        return null;
      }

      return getTaskSchedulingRecommendation(taskId);
    },
    enabled: Boolean(taskId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  return {
    queryKey,
    recommendation: query.data ?? null,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
  };
}

export function useRegenerateTaskSchedulingRecommendation(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => regenerateTaskSchedulingRecommendation(taskId),
    onSuccess: (recommendation) => {
      queryClient.setQueriesData({ queryKey: getTaskSchedulingRecommendationQueryKey(taskId) }, recommendation);

      toast({
        title: "Recommendation regenerated",
        description: "The task now uses the new scheduling recommendation.",
      });
    },
    onError: (error) => {
      logger.error("Error regenerating task scheduling recommendation", { taskId, error });

      toast({
        title: "Couldn't regenerate recommendation",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useVerifySchedulingEligibilityTask(taskId: string, displayId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: VerifySchedulingRecommendationInput) =>
      verifySchedulingEligibilityTask(taskId, payload),
    onSuccess: (updatedTask, payload) => {
      invalidateTaskCaches(queryClient, { id: updatedTask.id, displayId: updatedTask.displayId ?? displayId });

      // The endpoint returns the task, so apply the verification payload to the pinned recommendation
      // cache. Reopening this task then shows both the same row and its latest verification state.
      queryClient.setQueriesData<SchedulingRecommendation | null>(
        { queryKey: getTaskSchedulingRecommendationQueryKey(taskId) },
        (existing) =>
          existing?.id === payload.recommendationId
            ? { ...existing, isCorrect: payload.isCorrect, verificationReason: payload.verificationReason }
            : existing
      );

      toast({ title: "Verification saved", description: "The task has been completed." });
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: getTaskSchedulingRecommendationQueryKey(taskId) });
      logger.error("Error verifying scheduling eligibility task", { taskId, error });

      toast({
        title: "Couldn't save verification",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

// Creates a common onError handler for task mutations
const createTaskErrorHandler = (queryClient: QueryClient, filters: TaskFilters, errorMessage: string) => {
  return (error: Error, variables: any, context: any) => {
    logger.error(`Error: ${errorMessage}`);

    // Restore previous data for non-paginated tasks
    if (context?.previousTasks) {
      queryClient.setQueryData([TASKS_CACHE_KEY, filters], context.previousTasks);
    }

    // For paginated tasks, we need to invalidate since we don't store previous paginated state
    // This ensures fresh data is fetched after rollback
    queryClient.invalidateQueries({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] });

    toast({
      title: errorMessage,
      description: error.message,
      variant: "destructive",
    });
  };
};

// Creates a success toast with an optional action button to view the task
const createTaskSuccessToast = (title: string, description: (data: Task, variables: any) => string) => {
  return (data: Task, variables: any) => {
    toast({
      title,
      description: description(data, variables),
      action: (
        <ToastAction altText="View task" onClick={() => (window.location.href = `/tasks/${data.displayId}`)}>
          View task
        </ToastAction>
      ),
    });
  };
};

export function useTaskUpdateMutation() {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationFn: (task: Task) => {
      return updateTask(task);
    },
    onMutate: (task) => {
      const handler = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
        task: () => task,
        metadata: () => taskToTaskMetadata(task),
      });

      return handler({ ...task, taskId: task.id });
    },
    onError: createTaskErrorHandler(
      queryClient,
      taskFilters,
      "Something went wrong updating the task. Please refresh the page and try again."
    ),
  });
}

export function useResetSplitDocumentConfidenceMutation(task: Task) {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationFn: (fileId: string) => resetSplitDocumentConfidence(fileId),
    onMutate: async (fileId) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [TASKS_CACHE_KEY] }),
        queryClient.cancelQueries({ queryKey: [TASK_CACHE_KEY, task.displayId] }),
        queryClient.cancelQueries({ queryKey: [TASK_BY_ID_CACHE_KEY, task.id] }),
      ]);

      const previousTasks = queryClient.getQueryData<Record<string, Task>>([TASKS_CACHE_KEY, taskFilters]);
      const previousTaskByDisplayId = queryClient.getQueryData<Task>([TASK_CACHE_KEY, task.displayId]);
      const previousTaskById = queryClient.getQueryData<Task>([TASK_BY_ID_CACHE_KEY, task.id]);
      const currentTask = previousTaskByDisplayId ?? previousTaskById ?? task;
      const updatedTask = {
        ...currentTask,
        files: currentTask.files.map((file) =>
          file.id === fileId
            ? {
                ...file,
                reviewedSplitDocumentConfidence: null,
                intakeSectionGrouping: null,
                reviewedIntakeSectionGrouping: null,
              }
            : file
        ),
      };

      updateTasksQueryData(queryClient, updatedTask, taskFilters);

      return { previousTasks, previousTaskByDisplayId, previousTaskById };
    },
    onError: (_error, _fileId, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData([TASKS_CACHE_KEY, taskFilters], context.previousTasks);
      }

      if (context?.previousTaskByDisplayId) {
        queryClient.setQueryData([TASK_CACHE_KEY, task.displayId], context.previousTaskByDisplayId);
      }

      if (context?.previousTaskById) {
        queryClient.setQueryData([TASK_BY_ID_CACHE_KEY, task.id], context.previousTaskById);
      }

      toast({
        title: "Error",
        description: "Failed to revert classification changes. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => invalidateTaskCaches(queryClient, { id: task.id, displayId: task.displayId }),
  });
}

export function useLocalTaskUpdateMutation(options?: { invalidateOnSettled?: boolean }) {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    // This does a client side update only and assumes the task will be updated in the database separately
    mutationFn: (task: Task) => Promise.resolve(task),
    onMutate: (task) => {
      const handler = createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
        task: () => task,
        metadata: () => taskToTaskMetadata(task),
      });

      return handler({ ...task, taskId: task.id });
    },
    onError: createTaskErrorHandler(
      queryClient,
      taskFilters,
      "Something went wrong updating the task. Please refresh the page and try again."
    ),
    // Need to force an update on all task queries since server is updating the task in a job
    onSettled: (data) => {
      if (options?.invalidateOnSettled === false) return;
      invalidateTaskCaches(queryClient, { id: data?.id, displayId: data?.displayId });
    },
  });
}

export function useTaskStatusMutation() {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationKey: TASK_COMPLETION_MUTATION_KEY,
    mutationFn: ({ taskId, newStatus }: { taskId: string; newStatus: TaskStatus }) => {
      return updateStatus({ taskId, newStatus });
    },
    onMutate: createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: ({ newStatus }) => ({
        status: newStatus,
      }),
      metadata: ({ newStatus }) => ({
        status: newStatus,
      }),
    }),
    onSuccess: (data: Task, variables) => {
      // Skip toast for certain statuses to limit notifications
      if (data.status !== TaskStatus.COMPLETED && data.status !== TaskStatus.PROCESSING) {
        createTaskSuccessToast(
          "Status updated",
          (data) => `${data.displayId} status changed to ${pastTenseStatus[data.status]}`
        )(data, variables);
      }
    },
    onError: createTaskErrorHandler(
      queryClient,
      taskFilters,
      "Something went wrong updating the status. Please refresh the page and try again."
    ),
    onSettled: (data, _error, variables) =>
      invalidateTaskCaches(queryClient, { id: variables?.taskId, displayId: data?.displayId }),
  });
}

export function useVerifyAndCompleteMutation({ onError }: { onError?: () => void } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: TASK_COMPLETION_MUTATION_KEY,
    mutationFn: ({
      taskId,
      displayId,
      verificationResult,
      reason,
      details,
    }: {
      taskId: string;
      displayId: string | null;
      verificationResult: VerificationResult;
      reason?: string | null;
      details?: string | null;
    }) => verifyAndComplete(taskId, { verificationResult, reason, details }),
    onError,
    onSettled: (_data, _error, { taskId, displayId }) => invalidateTaskCaches(queryClient, { id: taskId, displayId }),
  });
}

export function useSendReferringProviderEmailMutation() {
  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => sendReferringProviderEmail(taskId),
  });
}

export function useAssignedUserMutation() {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationFn: async ({
      taskId,
      userId,
      overwrite = true,
    }: {
      taskId: string;
      userId: string | null;
      overwrite?: boolean;
    }) => {
      const task: Task | null = await updateAssignedUser({ taskId, userId, overwrite });

      if (!task) throw new Error("Task is already assigned to another user.");

      return task;
    },
    onMutate: (variables: { taskId: string; userId: string | null; overwrite?: boolean }) => {
      // For overwrite === false (e.g. race-safe auto-assignment), avoid optimistic updates
      // so the UI only updates once the server confirms the assignment.
      if (variables.overwrite === false) {
        return {};
      }

      const handler = createOptimisticTaskUpdateHandler<{ taskId: string; userId: string | null; overwrite?: boolean }>(
        queryClient,
        taskFilters,
        {
          task: (vars, originalTask) => ({
            currentAssignedToUserId: vars.userId,
            originalAssignedToUserId: originalTask.originalAssignedToUserId ?? vars.userId,
            assignedAt: new Date().toISOString(),
          }),
          metadata: (vars) => ({
            currentAssignedToUserId: vars.userId,
            assignedAt: new Date().toISOString(),
          }),
        }
      );

      return handler({ ...variables, taskId: variables.taskId });
    },
    onSuccess: (data) => {
      updateTasksQueryData(queryClient, data, taskFilters);
    },
    onError: (error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [TASKS_CACHE_KEY, taskFilters] });

      // Show specific toast for race condition when overwrite is false
      if (variables.overwrite === false) {
        toast({
          title: "Task unavailable",
          description: "This task was already claimed or completed. Please select a different task.",
          variant: "default",
        });

        return;
      }

      // Use the standard error handler which includes rollback
      return createTaskErrorHandler(queryClient, taskFilters, "Assignment update failed")(error, variables, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_FACET_COUNTS_CACHE_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] });
      queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY] });
    },
  });
}

export function useUnassignAllMyTasksMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => unassignAllMyTasks(),
    onSuccess: (unassignedTaskCount: number) => {
      toast({
        title: unassignedTaskCount === 0 ? "No matching tasks" : "Tasks unassigned",
        description:
          unassignedTaskCount === 0
            ? "You aren't assigned to any open, failed, or paused tasks in this customer."
            : `You were unassigned from ${unassignedTaskCount} ${unassignedTaskCount === 1 ? "task" : "tasks"}.`,
      });
    },
    onError: () => {
      toast({
        title: "Couldn't unassign tasks",
        description: "Please refresh the page and try again.",
        variant: "destructive",
      });
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] }),
        queryClient.invalidateQueries({ queryKey: [TASK_FACET_COUNTS_CACHE_KEY] }),
        queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY] }),
        queryClient.invalidateQueries({ queryKey: [TASKS_CACHE_KEY] }),
      ]),
  });
}

export function useTaskPriorityMutation() {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; priority: TaskPriority }) => {
      return updatePriority({ taskId, priority });
    },
    onMutate: createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: ({ priority }) => ({
        priority,
      }),
      metadata: ({ priority }) => ({
        taskPriority: priority,
      }),
    }),
    onSuccess: createTaskSuccessToast(
      "Task priority updated",
      (data) => `${data.displayId} priority updated to ${data.priority}`
    ),
    onError: createTaskErrorHandler(queryClient, taskFilters, "Priority update failed"),
    onSettled: (data, _error, variables) => {
      invalidateTaskCaches(queryClient, { id: variables?.taskId, displayId: data?.displayId });
    },
  });
}

export function useUpdateTaskNoteMutation() {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationFn: ({ taskId, note }: { taskId: string; note: string | null }) => {
      return updateTaskNote({ taskId, note });
    },
    onMutate: createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: ({ note }, originalTask) => {
        if (originalTask.patientOrder) {
          return { patientOrder: { ...originalTask.patientOrder, note } };
        }

        const updatedFiles = originalTask.files.map((file) =>
          file.type === FileType.INBOUND ? { ...file, note } : file
        );

        return { files: updatedFiles };
      },
      metadata: ({ note }) => ({
        note,
      }),
    }),
    onSuccess: (task, { note }) => {
      if (note === null || note === "") {
        toast({ title: "Note deleted successfully." });
      }

      if (!task.patientOrder) return;

      void queryClient.invalidateQueries({ queryKey: [PAGINATED_ORDERS_CACHE_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SINGLE_ORDER_CACHE_KEY] });
      void queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY] });
      void queryClient.invalidateQueries({ queryKey: [TASK_BY_ID_CACHE_KEY] });
    },
    onError: createTaskErrorHandler(queryClient, taskFilters, "Note update failed"),
  });
}

export function useTaskTagsMutation() {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationFn: ({ taskId, tags }: { taskId: string; tags: string[] }) => {
      return updateTaskTags({ taskId, tags });
    },
    onMutate: createOptimisticTaskUpdateHandler(queryClient, taskFilters, {
      task: ({ tags }) => ({
        tags: tags.length > 0 ? tags : null,
      }),
      metadata: ({ tags }) => ({
        tags: tags.length > 0 ? tags : null,
      }),
    }),
    onError: createTaskErrorHandler(queryClient, taskFilters, "Tag update failed"),
    onSettled: (data, _error, variables) =>
      invalidateTaskCaches(queryClient, { id: variables?.taskId, displayId: data?.displayId }),
  });
}

export const TASK_HISTORY_CACHE_KEY = "task-history";

export function useTaskHistory(taskId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [TASK_HISTORY_CACHE_KEY, taskId],
    queryFn: () => {
      if (!taskId) return null;

      return getTaskHistory(taskId);
    },
    enabled: options?.enabled !== false && !!taskId,
    staleTime: 30 * 1000,
  });
}

export function useDeleteTaskMutation() {
  const queryClient = useQueryClient();
  const [taskFilters] = useTaskFilters();

  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => {
      return deleteTask({ taskId });
    },
    onMutate: async ({ taskId }) => {
      await queryClient.cancelQueries({ queryKey: [TASKS_CACHE_KEY] });

      await queryClient.cancelQueries({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] });
      const previousTasks = queryClient.getQueryData<Record<string, Task>>([TASKS_CACHE_KEY, taskFilters]) || {};

      queryClient.setQueryData([TASKS_CACHE_KEY, taskFilters], (tasks: Record<string, Task> = {}) => {
        const updatedTasks = { ...tasks };

        delete updatedTasks[taskId];

        return updatedTasks;
      });

      // Also remove from paginated tasks metadata cache
      const paginatedMetadataQueries = queryClient.getQueryCache().findAll({
        queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY],
      });

      paginatedMetadataQueries.forEach((query) => {
        const queryData = queryClient.getQueryData<PaginatedTasksMetadataResponse>(query.queryKey);

        if (queryData?.tasks && queryData.tasks[taskId]) {
          const updatedTasks = { ...queryData.tasks };

          delete updatedTasks[taskId];

          const updatedPaginatedMetadataData = {
            ...queryData,
            tasks: updatedTasks,
          };

          queryClient.setQueryData(query.queryKey, updatedPaginatedMetadataData);
        }
      });

      queryClient.invalidateQueries({
        queryKey: [TASKS_CACHE_KEY],
        refetchType: "inactive",
      });

      return { previousTasks };
    },
    onError: (error, { taskId }, context) => {
      if (context) {
        queryClient.setQueryData([TASKS_CACHE_KEY, taskFilters], context.previousTasks);

        queryClient.invalidateQueries({ queryKey: [PAGINATED_TASKS_METADATA_CACHE_KEY] });
      }

      logger.error("Error deleting task:", { taskId, error });

      toast({
        title: "Task deletion failed",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_FACET_COUNTS_CACHE_KEY] });
    },
  });
}

export const CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY = "can-resume-awaiting-information";
const WORKFLOW_STEP_RESUME_CACHE_KEY = "workflow-step-resume";

export function useWorkflowStepResumeCheck(task: Pick<Task, "id" | "updatedAt">) {
  return useQuery({
    queryKey: [WORKFLOW_STEP_RESUME_CACHE_KEY, task.id, task.updatedAt],
    queryFn: () => getWorkflowStepResumeCheck(task.id),
  });
}

export function useResumeWorkflowStepMutation(task: Pick<Task, "id" | "displayId">) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskStepId: string) => resumeWorkflowStep(task.id, { taskStepId }),
    onSettled: async () => {
      await Promise.all([
        invalidateTaskCaches(queryClient, task),
        queryClient.invalidateQueries({ queryKey: [WORKFLOW_STEP_RESUME_CACHE_KEY, task.id] }),
        queryClient.invalidateQueries({ queryKey: [CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY, task.id] }),
      ]);
    },
  });
}

/** Only meaningful for a task sitting at AWAITING_INFORMATION; pass `enabled: false` otherwise. */
export function useCanResumeAwaitingInformationTask(taskId: string, options?: { enabled?: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY, taskId],
    queryFn: () => getCanResumeAwaitingInformationTask(taskId),
    enabled: options?.enabled ?? true,
  });

  return { resumable: data?.resumable ?? false, blockedReason: data?.blockedReason, isLoading };
}

export function useResumeAwaitingInformationTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => resumeAwaitingInformationTask(taskId),
    onSuccess: (result, taskId) => {
      queryClient.invalidateQueries({ queryKey: [TASK_CACHE_KEY, taskId] });
      queryClient.invalidateQueries({ queryKey: [TASK_BY_ID_CACHE_KEY, taskId] });
      queryClient.invalidateQueries({ queryKey: [CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY, taskId] });

      if (result.blockedReason) {
        toast({ title: "Task not resumed", description: result.blockedReason, variant: "destructive" });
      } else {
        invalidateTaskCaches(queryClient, { id: taskId });
      }
    },
    onError: (error, taskId) => {
      logger.error("Error resuming awaiting-information task:", { taskId, error });

      toast({
        title: "Failed to resume task",
        description: "Please refresh the page and try again.",
        variant: "destructive",
      });
    },
  });
}

export function useConfirmInsuranceNetworkMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      scope,
    }: {
      taskId: string;
      displayId: string | null;
      scope: ConfirmInsuranceNetworkRequest;
    }) => confirmInsuranceNetwork(taskId, scope),
    onSettled: async (_data, _error, { taskId, displayId }) => {
      await Promise.all([
        invalidateTaskCaches(queryClient, { id: taskId, displayId }),
        queryClient.invalidateQueries({ queryKey: getTaskSchedulingRecommendationQueryKey(taskId) }),
      ]);
    },
  });
}
